// Vorrang-Palette (Plan 2026-10-18): der Leiter bestimmt je Artikel EINE Palette, von der
// „sofort“ oder „als nächstes“ (nach der offenen Palette) gepickt wird. Ohne Markierung
// wählt die Automatik wie bisher.
import type { ArtikelPosition } from '@/api/warehouse'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export type VorrangModus = 'sofort' | 'naechste'
export const vorrangText: Record<VorrangModus, string> = { sofort: 'sofort', naechste: 'als nächstes' }

/** Dieselben Bedingungen wie die Pick-Kandidaten im Backend (fillup.ts): Etikett + Menge > 0. */
export function istPickQuelle(p: ArtikelPosition): boolean {
  if (p.packplatz) return false
  if (p.labelId === null) return false
  if (p.state === 'verschlossen') return (p.qtyExact ?? 0) > 0
  if (p.state === 'ausgepackt') return (p.qtyCirca ?? 0) > 0
  return false
}

function Punkt({ modus }: { modus: VorrangModus | null }) {
  // sofort = voller Punkt, als nächstes = Ring mit Kern, frei = leerer Ring
  if (modus === 'sofort') return <span aria-hidden className="size-2.5 shrink-0 rounded-full bg-primary" />
  if (modus === 'naechste') return <span aria-hidden className="grid size-2.5 shrink-0 place-items-center rounded-full ring-2 ring-primary"><span className="size-1 rounded-full bg-primary" /></span>
  return <span aria-hidden className="size-2.5 shrink-0 rounded-full ring-[1.5px] ring-muted/70 group-hover:ring-primary" />
}

export function VorrangZelle({ p, lead, busy, onSetzen, onAufheben }: {
  p: ArtikelPosition; lead: boolean; busy: boolean
  onSetzen: (modus: VorrangModus) => void; onAufheben: () => void
}) {
  const modus = p.vorrang?.modus ?? null
  const gesetzt = p.vorrang ? `gesetzt${p.vorrang.setByName ? ` von ${p.vorrang.setByName}` : ''}, ${new Date(p.vorrang.setAt).toLocaleDateString('de-DE')}` : undefined
  const anzeige = <>
    <Punkt modus={modus} />
    {modus && <span className="text-xs font-medium text-ink">{vorrangText[modus]}</span>}
  </>
  const setzbar = istPickQuelle(p)
  // Eine bestehende Markierung kann der Leiter immer aufheben, auch wenn die Palette gerade
  // in Bewegung oder leer ist; neu setzen nur auf Paletten, von denen gepickt werden kann.
  if (!lead || (!setzbar && !modus)) {
    return modus ? <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title={gesetzt}>{anzeige}</span> : null
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger disabled={busy} aria-label={`Vorrang für ${p.paletteNo ?? `Position ${p.id}`} wählen`} title={gesetzt}
        className="group inline-flex min-h-7 items-center gap-1.5 whitespace-nowrap rounded px-1.5 -ml-1.5 hover:bg-panel-2 focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50">
        {anzeige}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted font-normal">Von {p.paletteNo ?? 'dieser Palette'} nehmen …</DropdownMenuLabel>
        <DropdownMenuItem disabled={!setzbar || modus === 'sofort'} onSelect={() => onSetzen('sofort')} className="flex-col items-start gap-0">
          <span>Sofort nehmen</span>
          <span className="text-xs text-muted">auch wenn eine andere Palette offen ist</span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!setzbar || modus === 'naechste'} onSelect={() => onSetzen('naechste')} className="flex-col items-start gap-0">
          <span>Als nächstes nehmen</span>
          <span className="text-xs text-muted">wenn die offene Palette leer ist</span>
        </DropdownMenuItem>
        {modus && <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onAufheben}>Vorrang aufheben</DropdownMenuItem>
        </>}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
